// #region imports
import * as React from 'react';
import {
  reduxForm,
  getFormValues,
  InjectedFormProps,
  change
} from 'redux-form';
import { connect, useDispatch } from 'react-redux';

// helpers
import { compose } from '../../../../../utils/component-helpers';

// state
import {
  useCreateResource__ALPHA,
  useMappedId__ALPHA
} from '@cube3/state/src/redux/components/Hooks/useCreateResource';
import { addFormId } from '@cube3/state/src/redux/middleware/redux-form-middleware';
import { withModalActions, ModalActionProps } from '../../Modals/modalActions';
import withCurrentWorkspace, {
  useCurrentWorkspace,
  WorkspaceProps
} from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';

// UI
import AddProjectModalUI from '@cube3/ui/src/Projects/Modals/AddProjectModalUI';
import {
  makeValidator,
  isRequired,
  maxCharacters
} from '../../../../forms/helpers/validators';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import {
  RequestStatuses,
  statuses
} from '@cube3/state/src/redux/ducks/request-status';
import {
  hasError,
  serverErrorCodes as SERROR
} from '@cube3/state/src/wrapped-cube-client';
import { uuidv4 } from '@cube3/state/src/utils/uuid';
import { useMemo } from 'react';
import { generatePath, useHistory } from 'react-router';
import { urlStructureProject } from '../../../routing/routingPaths';
import { useCurrentAccount } from '@cube3/state/src/redux/components/Administration/withCurrentUserAccount';
import {
  ProjectIconType,
  projectIconName
} from '@cube3/common/model/schema/resources/project';
import { Icon } from '@cube3/cubicle/src/core/atoms/Icon';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';
import { ProjectTemplate } from '@cube3/common/model/schema/resources/project-template';

import { useFeatures } from '@cube3/state/src/redux/components/Hooks/useFeatures';
import { Feature, ProjectStatuses } from '@cube3/common/model/resource-types';
import { useProjectMetadataFields } from '../hooks/useProjectMetadataFields';
import { useMutateResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useMutateResource';
import { useDeleteResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useDeleteResource';
import { getErrorMessage } from '@cube3/forms/src/utils/getErrorMessage';
import { useMultiForm } from '../../MetaData/hooks/useMultiForm';
import { hasResolved } from '@cube3/state/src/redux/ducks/request-status/utils/hasResolved';
import { useDeleteMetadataValues } from '../hooks/useDeleteMetadataValues';
import { GenericReduxForm } from '@cube3/forms/src/forms/GenericReduxForm';
import { Field } from '@cube3/forms/src/types/GenericFormTypes';

const requiredFeatures = {
  features: ['/WORKSPACE/LABELING'] as Feature[]
};

// #region interfaces

interface ProjectTemplateOption {
  id: string;
  display_name: string;
  icon?: number;
  value?: string;
  relationships: {
    doner_project: {
      type: 'project';
      id: string;
    };
  };
}

type FormValues = {
  form: string;
  projectName?: string;
  projectTemplate: ProjectTemplateOption;
  projectIcon: { prefix: React.ReactNode; display_name: ProjectIconType };
  allowLabeling?: boolean;
};
interface HOCMapped {
  handleSubmit: () => void;

  addProject?: Function;
  updateProject?: Function;
  addProjectStatus?: string;
  updateProjectStatus?: string;
  projectTemplateOptions: ProjectTemplateOption[];
  loading?: boolean;
  tempId?: string;
  /** If allows to use no project template */
  allowEmpty?: boolean;
}

interface ModalReceiverProps {
  modalContext: {
    draftProjectId: string;
    /** The project name based on filled metadata values. E.g.: `{{ metadata.1 }}__{{ metadata.2 }}_{{ metadata.3 }}` */
    prefilledProjectName: string;
    /** The metadata values to pre-fill based on the selected metadata filters in the projects overview page */
    filterValues: { [metadataName: string]: string };
  };
}

type Properties = HOCMapped &
  FormValues &
  InjectedFormProps &
  ModalActionProps &
  ModalReceiverProps &
  WorkspaceProps;
// #endregion

const projectIcons = [
  {
    id: 'Project',
    display_name: 'Project',
    value: projectIconName.Project,
    startAdornment: <Icon icon={projectIconName.Project} />
  },
  {
    id: 'Creative Automation',
    display_name: 'Creative Automation',
    value: projectIconName['Creative Automation'],
    startAdornment: <Icon icon={projectIconName['Creative Automation']} />
  },
  {
    id: 'Library',
    display_name: 'Library',
    value: projectIconName.Library,
    startAdornment: <Icon icon={projectIconName.Library} />
  }
];

const mapStateToProps = (state, ownProps) => {
  const values = getFormValues('add_project')(state);
  return {
    form: ownProps.form,
    formValues: values,
    initialValues: {
      ...ownProps.initialValues,
      projectIcon: projectIcons[0]
    },
    ...values
  };
};

export const getErrorText = (e) => {
  if (!e) {
    return;
  }

  switch (true) {
    case hasError(e, SERROR.ATTRIBUTE_UNIQUE_CONSTRAINT):
      return 'A Project with this name already exists. Please choose a different name, that is unique within the workspace.';
    default:
      return (
        getErrorMessage(e[0]) ||
        'Unknown server error... if this problem persists please contact support@cube-cloud.com'
      );
  }
};

const EMPTY_TYPE = 'EMPTY_TYPE';

const maxChars = (value: string) => maxCharacters(value, 128);
const stackErrors = makeValidator([isRequired, maxChars]);

const AddProjectModal: React.FC<Properties> = React.memo((props) => {
  const {
    projectTemplateOptions,
    handleSubmit,
    openModal,
    previousModal,
    closeAllModals,
    submitting,
    submitSucceeded,
    loading,
    valid,
    error,
    anyTouched,
    modalContext,
    tempId,
    currentWorkspaceId,

    addProject,
    addProjectStatus,
    updateProjectStatus,
    allowEmpty,
    // form values
    projectTemplate,
    projectName,
    projectIcon,
    allowLabeling
  } = props;

  const [hasLabelingFeature] = useFeatures(requiredFeatures);
  const [deleteResource, deleteResourceStatus] = useDeleteResource__ALPHA({
    cacheInvalidator: (r) => [r]
  });

  const { filterValues, draftProjectId } = modalContext;

  const { retrievedForms, prefillMetadataCheck: shouldEditMetadata } =
    useProjectMetadataFields({
      filterValues
    });
  const { destroyForms, allFormValues } = useMultiForm(retrievedForms);

  /** If there's required metadata fields,
   * - 1. trigger a dry run to check the collisions with existing projects,
   */
  const onNext = React.useCallback(() => {
    addProject({
      type: 'project',
      display_name: projectName,
      icon: projectIcons.findIndex(
        (p) => p.display_name === projectIcon?.display_name
      ),
      dry_run: true,
      allow_labeling: allowLabeling
    });
  }, [addProject, projectName, projectIcon, allowLabeling]);

  /** - 2. if passes the dry run,
   *    - if the modal opens after `EditNewProjectMetadataModal`, should submit to update draft project,
   *    - otherwise go to edit metadata fields modal
   */
  React.useEffect(() => {
    if (addProjectStatus === statuses.SUCCESS) {
      if (draftProjectId) {
        handleSubmit();
      } else {
        openModal(
          'metadata_edit_required_fields',
          {
            createProject: handleSubmit,
            filterValues,
            tempId: tempId
          },
          false
        );
      }
    }
  }, [addProjectStatus, draftProjectId, filterValues, handleSubmit, tempId]);

  /** Delete draft project if user cancels submit or clicks outside of the modal */
  const onClose = React.useCallback(() => {
    if (draftProjectId) {
      deleteResource({
        type: 'project',
        id: draftProjectId
      });
      destroyForms();
    }
    closeAllModals();
  }, [draftProjectId, deleteResource]);

  // close modal after deleting is completed
  React.useEffect(() => {
    if (draftProjectId && deleteResourceStatus === statuses.SUCCESS) {
      closeAllModals();
    }
  }, [draftProjectId, deleteResourceStatus]);

  const history = useHistory();
  const mappedId = useMappedId__ALPHA(tempId);

  /** Close modal and redirect to the new project after creation is succeeded.
   * - if there's no required metadata fields,
   * - or finish updating the draft project.
   *  */
  React.useEffect(() => {
    if (
      (!shouldEditMetadata.should && submitSucceeded) ||
      (draftProjectId && updateProjectStatus === statuses.SUCCESS)
    ) {
      closeAllModals();
      history.push(
        generatePath(urlStructureProject, {
          workspaceId: currentWorkspaceId,
          projectId: draftProjectId || mappedId
        })
      );
      destroyForms();
    }
  }, [
    updateProjectStatus,
    draftProjectId,
    shouldEditMetadata,
    submitSucceeded,
    history,
    currentWorkspaceId,
    mappedId
  ]);

  // update icon when changing project template
  const dispatch = useDispatch();
  React.useEffect(() => {
    if (
      !projectTemplate ||
      projectTemplate.id === EMPTY_TYPE ||
      !projectTemplateOptions?.length
    )
      return;
    const icon = projectTemplateOptions.find(
      (t) => t.id === projectTemplate.id
    )?.icon;
    const iconUI = projectIcons[icon];
    dispatch(change('add_project', 'projectIcon', iconUI));
  }, [projectTemplate, projectTemplateOptions, dispatch, change]);

  // close all modals after updating draft project properties is complete
  React.useEffect(() => {
    if (updateProjectStatus === statuses.SUCCESS) {
      closeAllModals();
    }
  }, [updateProjectStatus]);

  const [onDelete, deleteMetadataValuesStatus] = useDeleteMetadataValues(
    draftProjectId,
    allFormValues
  );
  /** Delete saved metadata-values to prevent conflict error from submiting the same values again */
  const onClickPrevious = React.useCallback(() => {
    onDelete();
  }, [allFormValues, deleteResource]);

  React.useEffect(() => {
    if (deleteMetadataValuesStatus === statuses.SUCCESS) previousModal();
  }, [deleteMetadataValuesStatus]);

  const fields = useMemo(() => {
    const width = '640px';
    const direction = 'horizontal';
    return [
      {
        type: 'string',
        name: 'projectName',
        label: 'Project name',
        placeholder: 'Project name',
        direction: direction,
        width: width,
        autoFocus: true,
        showErrors: anyTouched,
        validate: stackErrors,
        required: true
      } as Field<string, 'string'>,
      {
        type: 'select',
        name: 'projectIcon',
        label: 'Thumbnail',
        placeholder: 'Select a thumbnail...',
        direction: direction,
        options: projectIcons,
        width: width,
        showClearButton: false
      } as Field<string, 'select'>,
      {
        type: 'select',
        name: 'projectTemplate',
        label: 'Template',
        placeholder: 'Select a template...',
        showErrors: anyTouched,
        direction: direction,
        options: projectTemplateOptions,
        width: width,
        showClearButton: false,
        required: true,
        validate: allowEmpty
          ? (val) => (!val ? 'This field is required' : undefined)
          : (val) =>
              !val || val.id === EMPTY_TYPE
                ? 'This field is required'
                : undefined
      } as Field<string, 'select'>,
      hasLabelingFeature &&
        ({
          type: 'checkbox',
          name: 'allowLabeling',
          label: 'AI Tagging',
          direction: direction
        } as Field<boolean, 'checkbox'>)
    ].filter(Boolean);
  }, [hasLabelingFeature, anyTouched, allowEmpty, projectTemplateOptions]);

  return (
    <AddProjectModalUI
      submitButtonLabel={
        shouldEditMetadata.should && !draftProjectId ? 'Next' : 'Create'
      }
      onSubmit={shouldEditMetadata.should ? onNext : handleSubmit}
      onCloseEvent={onClose}
      valid={valid && !error}
      error={getErrorText(error)}
      loading={
        loading ||
        (deleteMetadataValuesStatus && !hasResolved(deleteMetadataValuesStatus))
      }
      submitting={submitting}
      onClickPrevious={draftProjectId ? onClickPrevious : undefined}
      fields={
        <GenericReduxForm
          fields={fields}
          weight="label"
          colorVariant="01"
          textAlign="left"
        />
      }
    />
  );
});

const withCreateProject = (Wrapped) => (props) => {
  const [accountId] = useCurrentAccount();
  const { currentWorkspaceId } = props;
  const tempId = React.useRef(uuidv4());
  const config = useMemo(() => {
    return {
      resourceType: 'project' as const,
      ancestor: { type: 'workspace' as const, id: currentWorkspaceId },
      relationship: 'projects',
      actionDecorators: [
        addFormId('add_project', {
          formState: undefined,
          useRequestStatus: true,
          delegate: true
        })
      ],
      cacheInvalidator: () => {
        return [
          {
            type: 'workspace' as const,
            id: currentWorkspaceId,
            relationship: 'projects'
          },
          {
            type: 'account' as const,
            id: accountId,
            relationship: 'project-members'
          },
          {
            type: 'workspace' as const,
            id: currentWorkspaceId,
            relationship: 'search'
          }
        ];
      }
    };
  }, [currentWorkspaceId, accountId]);

  const [createResource, createResourceStatus] =
    useCreateResource__ALPHA(config);

  const [updateProject, updateProjectStatus] = useMutateResource__ALPHA(config);

  return (
    <Wrapped
      {...props}
      addProject={createResource}
      updateProject={updateProject}
      addProjectStatus={createResourceStatus}
      updateProjectStatus={updateProjectStatus}
      loading={
        props.loading ||
        (createResourceStatus && !hasResolved(createResourceStatus)) ||
        (updateProjectStatus && !hasResolved(updateProjectStatus))
      }
      tempId={tempId.current}
    />
  );
};

const withProjectTemplateOptions = (Wrapped) => (props) => {
  const [workspaceId, workspace, _, workspaceStatus] = useCurrentWorkspace();

  const projectTemplates = useResourceList__ALPHA({
    resourceType: 'workspace',
    resourceId: workspaceId,
    edgeType: 'project-template',
    edgeLabel: 'project-templates'
  });

  // use to get `icon` for each template
  const donorProjects = useResource__ALPHA({
    resourceIdentifiers: useMemo(
      () =>
        projectTemplates.resources?.map((p) => p.relationships.donor_project),
      [projectTemplates.resources]
    )
  }).resources;

  const allowEmpty = !workspace || !workspace.project_template_required;

  const projectTemplateOptions = React.useMemo(() => {
    const withEmptyOption = allowEmpty
      ? [
          { display_name: 'Use no template', id: EMPTY_TYPE },
          ...(projectTemplates.resources || [])
        ]
      : projectTemplates.resources || [];

    return withEmptyOption.map((option) => {
      const isDefault = option.id === workspace?.default_project_template_id;
      return {
        ...option,
        suffix: isDefault ? 'DEFAULT' : undefined,
        icon:
          option.id === EMPTY_TYPE
            ? 0
            : donorProjects?.find(
                (d) =>
                  d.id ===
                  (option as ProjectTemplate).relationships.donor_project.id
              )?.icon
      };
    });
  }, [projectTemplates.resources, allowEmpty, workspace, donorProjects]);

  const initializing =
    ([statuses.SUCCESS, statuses.FAILED] as RequestStatuses[]).indexOf(
      projectTemplates.status
    ) < 0 ||
    ([statuses.SUCCESS, statuses.FAILED] as RequestStatuses[]).indexOf(
      workspaceStatus
    ) < 0;

  const defaultType =
    workspace &&
    (projectTemplateOptions?.find(
      (t) => t.id === workspace.default_project_template_id
    ) ||
      (allowEmpty && { display_name: 'Use no template', id: EMPTY_TYPE }));

  const { prefilledProjectName } = props.modalContext;
  const initialValues = useMemo(() => {
    return {
      projectTemplate: defaultType,
      projectName: prefilledProjectName
    };
  }, [defaultType, prefilledProjectName]);

  return (
    <Wrapped
      enableReinitialize={initializing}
      {...props}
      loading={props.loading || initializing}
      initialValues={initialValues}
      projectTemplateOptions={projectTemplateOptions}
      allowEmpty={allowEmpty}
    />
  );
};

interface AddProjectFields {
  projectName: string;
  projectTemplate?: { id: string };
  allowLabeling?: boolean;
}

export default compose(AddProjectModal)(
  withCurrentWorkspace,
  withModalActions,
  withProjectTemplateOptions,
  connect(mapStateToProps, {}),
  withCreateProject,
  reduxForm<AddProjectFields, HOCMapped>({
    form: 'add_project',
    destroyOnUnmount: true,
    onSubmit: (values, dispatch, props) => {
      const { draftProjectId } = props.modalContext;

      const attributes = {
        type: 'project',
        display_name: values.projectName,
        icon: projectIcons.findIndex(
          (p) => p.display_name === values.projectIcon.display_name
        ),
        allow_labeling: values.allowLabeling,
        /** add project type if applicable */
        relationships: {
          project_template:
            values.projectTemplate?.id === EMPTY_TYPE
              ? undefined
              : {
                  data: {
                    id: values.projectTemplate.id,
                    type: 'project-template'
                  }
                }
        }
      };
      // Update the draft project with the new properties, replacing the draft status with the default project status
      if (draftProjectId) {
        props.updateProject({
          id: draftProjectId,
          project_status: ProjectStatuses.ProjectStatusOpen,
          ...attributes
        });
      } else {
        /** submit the request to create the actual project */
        props.addProject({
          temporaryId: props.tempId,
          ...attributes
        });
      }

      return new Promise((res) => null);
    }
  })
);
